import React, { useState } from 'react';
import './CalorieCalculator.css';

const CalorieCalculator = () => {
  const [formData, setFormData] = useState({
    gender: 'male',
    age: '',
    weight: '',
    height: '',
    activity: '1.375',
    goal: 'maintain'
  });

  const [results, setResults] = useState(null);

  const activityLevels = [
    { value: '1.2', label: 'Минимальная', description: 'Сидячий образ жизни' },
    { value: '1.375', label: 'Низкая', description: 'Легкие упражнения 1-3 раза в неделю' },
    { value: '1.55', label: 'Средняя', description: 'Тренировки 3-5 раз в неделю' },
    { value: '1.725', label: 'Высокая', description: 'Интенсивные тренировки 6-7 раз в неделю' },
    { value: '1.9', label: 'Очень высокая', description: 'Тяжелые тренировки 2 раза в день' }
  ];

  const goals = [
    { value: 'lose', label: 'Похудение', description: 'Дефицит 500 ккал/день (-0.5 кг/неделю)', color: '#0891b2' },
    { value: 'lose_fast', label: 'Быстрое похудение', description: 'Дефицит 750 ккал/день (-0.75 кг/неделю)', color: '#0284c7' },
    { value: 'maintain', label: 'Поддержание веса', description: 'Норма калорий', color: '#059669' },
    { value: 'gain', label: 'Набор массы', description: 'Профицит 300 ккал/день (+0.3 кг/неделю)', color: '#dc2626' },
    { value: 'gain_fast', label: 'Быстрый набор', description: 'Профицит 500 ккал/день (+0.5 кг/неделю)', color: '#b91c1c' }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const calculateCalories = (e) => {
    e.preventDefault();

    const { gender, age, weight, height, activity, goal } = formData;

    // Формула Миффлина-Сан Жеора
    let bmr;
    if (gender === 'male') {
      bmr = 10 * parseFloat(weight) + 6.25 * parseFloat(height) - 5 * parseFloat(age) + 5;
    } else {
      bmr = 10 * parseFloat(weight) + 6.25 * parseFloat(height) - 5 * parseFloat(age) - 161;
    }

    // TDEE (Total Daily Energy Expenditure)
    const tdee = Math.round(bmr * parseFloat(activity));

    // Калории в зависимости от цели
    let targetCalories;
    switch (goal) {
      case 'lose':
        targetCalories = tdee - 500;
        break;
      case 'lose_fast':
        targetCalories = tdee - 750;
        break;
      case 'maintain':
        targetCalories = tdee;
        break;
      case 'gain':
        targetCalories = tdee + 300;
        break;
      case 'gain_fast':
        targetCalories = tdee + 500;
        break;
      default:
        targetCalories = tdee;
    }

    // Макронутриенты
    const protein = Math.round(parseFloat(weight) * 2); // 2г на кг веса
    const fats = Math.round(parseFloat(weight) * 1); // 1г на кг веса
    const proteinCalories = protein * 4;
    const fatsCalories = fats * 9;
    const carbsCalories = targetCalories - proteinCalories - fatsCalories;
    const carbs = Math.round(carbsCalories / 4);

    setResults({
      bmr: Math.round(bmr),
      tdee,
      targetCalories: Math.round(targetCalories),
      protein,
      fats,
      carbs
    });
  };

  return (
    <div className="calorie-calculator">
      <div className="calculator-header">
        <h2>Калькулятор Калорий</h2>
        <p>Рассчитайте свою норму калорий и макронутриентов для достижения целей</p>
      </div>

      <div className="calculator-grid">
        {/* Form */}
        <div className="calculator-form">
          <form onSubmit={calculateCalories}>
            {/* Gender */}
            <div className="form-group">
              <label>Пол</label>
              <div className="gender-selector">
                <button
                  type="button"
                  className={`gender-btn ${formData.gender === 'male' ? 'active' : ''}`}
                  onClick={() => setFormData({ ...formData, gender: 'male' })}
                >
                  👨 Мужской
                </button>
                <button
                  type="button"
                  className={`gender-btn ${formData.gender === 'female' ? 'active' : ''}`}
                  onClick={() => setFormData({ ...formData, gender: 'female' })}
                >
                  👩 Женский
                </button>
              </div>
            </div>

            {/* Age */}
            <div className="form-group">
              <label htmlFor="age">Возраст</label>
              <input
                type="number"
                id="age"
                name="age"
                value={formData.age}
                onChange={handleChange}
                placeholder="Введите возраст"
                required
                min="15"
                max="100"
              />
            </div>

            {/* Weight */}
            <div className="form-group">
              <label htmlFor="weight">Вес (кг)</label>
              <input
                type="number"
                id="weight"
                name="weight"
                value={formData.weight}
                onChange={handleChange}
                placeholder="Введите вес"
                required
                min="30"
                max="300"
                step="0.1"
              />
            </div>

            {/* Height */}
            <div className="form-group">
              <label htmlFor="height">Рост (см)</label>
              <input
                type="number"
                id="height"
                name="height"
                value={formData.height}
                onChange={handleChange}
                placeholder="Введите рост"
                required
                min="100"
                max="250"
              />
            </div>

            {/* Activity Level */}
            <div className="form-group">
              <label htmlFor="activity">Уровень активности</label>
              <select
                id="activity"
                name="activity"
                value={formData.activity}
                onChange={handleChange}
                required
              >
                {activityLevels.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label} - {level.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Goal */}
            <div className="form-group">
              <label>Цель</label>
              <div className="goals-grid">
                {goals.map((goalOption) => (
                  <button
                    key={goalOption.value}
                    type="button"
                    className={`goal-btn ${formData.goal === goalOption.value ? 'active' : ''}`}
                    onClick={() => setFormData({ ...formData, goal: goalOption.value })}
                    style={{ '--goal-color': goalOption.color }}
                  >
                    <div className="goal-label">{goalOption.label}</div>
                    <div className="goal-description">{goalOption.description}</div>
                  </button>
                ))}
              </div>
            </div>

            <button type="submit" className="btn-primary btn-full">
              Рассчитать
            </button>
          </form>
        </div>

        {/* Results */}
        {results && (
          <div className="calculator-results">
            <h3>Ваши результаты</h3>

            <div className="result-card highlight">
              <div className="result-label">Целевая норма калорий</div>
              <div className="result-value large">{results.targetCalories} <span>ккал/день</span></div>
            </div>

            <div className="results-grid">
              <div className="result-card">
                <div className="result-label">Базовый метаболизм (BMR)</div>
                <div className="result-value">{results.bmr} <span>ккал</span></div>
              </div>
              <div className="result-card">
                <div className="result-label">Расход калорий (TDEE)</div>
                <div className="result-value">{results.tdee} <span>ккал</span></div>
              </div>
            </div>

            <div className="macros-section">
              <h4>Макронутриенты</h4>
              <p className="macros-note">Рекомендуемое распределение БЖУ для вашей цели</p>

              <div className="macro-item protein">
                <div className="macro-header">
                  <span className="macro-icon">🥩</span>
                  <span className="macro-name">Белки</span>
                </div>
                <div className="macro-value">{results.protein}г</div>
                <div className="macro-calories">{results.protein * 4} ккал</div>
              </div>

              <div className="macro-item fats">
                <div className="macro-header">
                  <span className="macro-icon">🥑</span>
                  <span className="macro-name">Жиры</span>
                </div>
                <div className="macro-value">{results.fats}г</div>
                <div className="macro-calories">{results.fats * 9} ккал</div>
              </div>

              <div className="macro-item carbs">
                <div className="macro-header">
                  <span className="macro-icon">🍚</span>
                  <span className="macro-name">Углеводы</span>
                </div>
                <div className="macro-value">{results.carbs}г</div>
                <div className="macro-calories">{results.carbs * 4} ккал</div>
              </div>
            </div>

            <div className="tips-section">
              <h4>💡 Полезные советы</h4>
              <ul>
                <li>Взвешивайтесь раз в неделю утром натощак</li>
                <li>Корректируйте калории каждые 2-4 недели</li>
                <li>Пейте 30-40 мл воды на 1 кг веса</li>
                <li>Не опускайте калории ниже BMR</li>
              </ul>
            </div>
          </div>
        )}

        {!results && (
          <div className="calculator-placeholder">
            <div className="placeholder-icon">🍎</div>
            <h3>Заполните форму</h3>
            <p>Введите ваши данные, чтобы рассчитать норму калорий и макронутриентов</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalorieCalculator;
